import React from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../../i18n';
import MarketingHeader from '../common/MarketingHeader';
import MarketingFooter from '../common/MarketingFooter';
import CtaBand from '../Home/CtaBand';
import GuideCta, { FreeNote } from '../common/GuideCta';
import { TAG_COLORS } from '../../../components/admin/RetroPanel/catalogMeta';
import { buildPlan } from '../../../components/admin/RetroPanel/buildPlan';
import { buildPeriods, Period, periodOf } from '../../../components/admin/RetroPanel/schedule';
import { RETRO_SIGNUP_URL } from '../signup/startIntent';
import appImage from '../../../assets/retroplanning-app.webp';
import { ADMIN, EXPRESS, FAQ, MILESTONES, MISTAKES, PERIOD_INTRO, READING_MINUTES, TOC, UPDATED } from './content';
import '../../../styles/marketing.css';
import { AUTHOR, AUTHOR_URL } from '../common/author';
import { AuthorBox, AuthorByline } from '../common/AuthorByline';
import '../../../styles/marketing-guide.css';

const TITLE = 'Rétroplanning mariage : le planning complet, mois par mois';
const updatedLabel = new Date(`${UPDATED}T12:00`).toLocaleDateString('fr-FR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

// Détail mois par mois : le rétroplanning « pas à pas » de l'app pour un mariage
// dans 18 mois, toutes cérémonies (calculé une fois, identique au pré-rendu).
const DETAIL_MONTHS = 18;
const DETAIL = (() => {
  const periods = buildPeriods(DETAIL_MONTHS);
  const tasks = buildPlan({
    months: DETAIL_MONTHS,
    level: 'detailed',
    ceremonies: ['civil', 'religieuse', 'laique'],
    statuses: {},
    hasGuests: false,
  });
  return periods
    .map((period) => ({ period, tasks: tasks.filter((t) => periodOf(periods, t.due) === period) }))
    .filter((g) => g.tasks.length > 0);
})();

const SHORT_PERIODS: Record<Exclude<Period['kind'], 'slice'>, string> = {
  m2: '2 mois avant',
  m1: '1 mois avant',
  w2: '2 semaines avant',
  w1: '1 semaine avant',
  d1: 'La veille',
};
const periodTitle = (p: Period) =>
  p.kind === 'slice' ? `${Math.round(p.to)} à ${Math.round(p.from)} mois avant` : SHORT_PERIODS[p.kind];

// Données structurées : article + FAQ (résultats enrichis Google)
const LD = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: TITLE,
    dateModified: UPDATED,
    datePublished: '2026-10-14',
    inLanguage: 'fr',
    author: { '@type': 'Person', name: AUTHOR.name, url: AUTHOR_URL },
    publisher: { '@type': 'Organization', name: 'RSVP Please' },
    image: 'https://www.please-rsvp.com/og-image.png',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  },
]);

const RetroCta: React.FC<{ title: string; text: string; label?: string }> = ({
  title,
  text,
  label = 'Créer mon rétroplanning gratuit',
}) => <GuideCta to={RETRO_SIGNUP_URL} title={title} text={text} label={label} />;

// Article « Rétroplanning mariage » : guide complet, mois par mois, avec des appels
// à créer son rétroplanning daté dans l'app. Pré-rendu au build.
const RetroGuidePage: React.FC = () => {
  const t = useT();
  return (
    <div className="mk-page">
      <MarketingHeader>
        <Link to="/login" className="mkt-btn ghost">
          {t('Se connecter')}
        </Link>
        <Link to="/signup" className="mkt-btn primary mk-header-signup">
          {t('Créer mon espace')}
        </Link>
      </MarketingHeader>

      <main className="mk-main" lang="fr">
        <article className="mg-article mk-container">
          <header className="mg-article-head">
            <span className="mk-eyebrow">Guide · Organisation</span>
            <h1>{TITLE}</h1>
            <AuthorByline updatedLabel={updatedLabel} readingMinutes={READING_MINUTES} />
            <p className="mg-article-lead">
              Lieu, traiteur, faire-part, mairie, plan de table : un mariage, c’est une quarantaine d’étapes qui
              s’enchaînent sur un an ou plus. Le rétroplanning les remet dans l’ordre, à rebours depuis le jour J. Voici
              le planning complet, période par période, avec les échéances qui comptent et les pièges à éviter.
            </p>
          </header>

          <RetroCta
            title="Votre rétroplanning, daté pour votre mariage"
            text="Indiquez votre date : toutes ces étapes sont calées sur votre jour J, à cocher au fil de l’eau, avec un e-mail au début de chaque période."
          />

          <nav className="mg-toc" aria-labelledby="mg-toc-title">
            <p id="mg-toc-title" className="mg-toc-title">
              Sommaire
            </p>
            <ol>
              {TOC.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>{s.label}</a>
                </li>
              ))}
            </ol>
          </nav>

          <section id="definition">
            <h2>Qu’est-ce qu’un rétroplanning de mariage ?</h2>
            <p>
              Un rétroplanning de mariage est la liste des étapes de l’organisation, classées <b>à rebours</b> depuis la
              date du mariage : ce qu’il faut faire 12 mois avant, 6 mois avant, 1 mois avant, jusqu’à la veille. On
              part du jour J et on remonte le temps, pour savoir à chaque moment ce qui doit être réglé.
            </p>
            <p>
              C’est la meilleure façon d’éviter les deux pièges classiques : s’y prendre trop tard pour les prestataires
              très demandés, et tout vouloir régler en même temps.
            </p>
          </section>

          <section id="quand-commencer">
            <h2>Quand commencer à organiser son mariage ?</h2>
            <p>
              L’idéal est de commencer <b>12 à 18 mois avant</b>. Les lieux de réception et les prestataires les plus
              demandés (photographes, traiteurs, groupes de musique) se réservent souvent plus d’un an à l’avance,
              surtout pour un samedi entre mai et septembre.
            </p>
            <p>
              Moins de temps devant vous ? Tout reste possible : les mêmes étapes, resserrées. Voir le{' '}
              <a href="#express">rétroplanning express</a>.
            </p>
          </section>

          <section id="resume">
            <h2>Le rétroplanning en résumé</h2>
            <ol className="mg-timeline">
              {MILESTONES.map((m) => (
                <li key={m.when}>
                  <span className="mg-when">{m.when}</span>
                  <h3>{m.title}</h3>
                  <p>{m.text}</p>
                </li>
              ))}
            </ol>
          </section>

          <section id="detail">
            <h2>Le rétroplanning détaillé, mois par mois</h2>
            <p>
              La check-list complète pour un mariage préparé 18 mois à l’avance, avec une cérémonie civile, religieuse
              ou laïque. Gardez ce qui vous concerne.
            </p>
            {DETAIL.map(({ period, tasks }) => (
              <div key={period.key} className="mg-period">
                <h3>{periodTitle(period)}</h3>
                {PERIOD_INTRO[period.key] && <p>{PERIOD_INTRO[period.key]}</p>}
                <ul className="mg-checklist">
                  {tasks.map((task) => (
                    <li key={task.key}>
                      <span className="mg-box" aria-hidden="true" />
                      <span className="mg-task">
                        {task.key === 'plan' ? <Link to="/plan-de-table-mariage">{task.label}</Link> : task.label}
                      </span>
                      <span className="mg-tag" style={{ background: TAG_COLORS[task.tag] ?? TAG_COLORS.Autre }}>
                        {task.tag}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>

          <figure className="mg-cta-figure">
            <img
              src={appImage}
              width={1100}
              height={1257}
              loading="lazy"
              decoding="async"
              alt="Rétroplanning de mariage dans l’espace RSVP Please : périodes datées, tâches cochées et rappel par e-mail"
            />
            <figcaption>
              <p className="mg-cta-title">Ce planning, daté et à cocher</p>
              <p>
                Dans votre espace, chaque étape est calée sur votre date. Vous cochez au fil de l’eau, ajoutez les
                vôtres, et recevez un e-mail au début de chaque période.
              </p>
              <Link to={RETRO_SIGNUP_URL} className="mkt-btn primary">
                Créer mon rétroplanning gratuit
              </Link>
              <FreeNote />
            </figcaption>
          </figure>

          <section id="express">
            <h2>Mariage dans 6 mois ou moins : le rétroplanning express</h2>
            <p>
              Organiser un mariage en 6 mois, voire en 3, est tout à fait faisable. Il faut simplement aller à
              l’essentiel, dans le bon ordre :
            </p>
            <ol className="mg-steps">
              {EXPRESS.map((s) => (
                <li key={s.title}>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </li>
              ))}
            </ol>
            <RetroCta
              title="Peu de temps devant vous ?"
              text="Votre rétroplanning s’adapte au temps qu’il reste : les étapes sont resserrées et priorisées d’après votre date."
              label="Créer mon rétroplanning express"
            />
          </section>

          <section id="demarches">
            <h2>Les démarches administratives</h2>
            {ADMIN.map((a) => (
              <div key={a.title} className="mg-admin">
                <h3>{a.title}</h3>
                <p>{a.text}</p>
              </div>
            ))}
          </section>

          <section id="erreurs">
            <h2>Les 6 erreurs qui font déraper un rétroplanning</h2>
            <ol className="mg-steps">
              {MISTAKES.map((m) => (
                <li key={m.title}>
                  <h3>{m.title}</h3>
                  <p>{m.text}</p>
                </li>
              ))}
            </ol>
          </section>

          <section id="faq">
            <h2>Questions fréquentes</h2>
            <div className="mg-faq">
              {FAQ.map(({ q, a }) => (
                <details key={q}>
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </section>

          <AuthorBox />

          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: LD }} />
        </article>

        <CtaBand />
      </main>

      <MarketingFooter />
    </div>
  );
};

export default RetroGuidePage;
